import styled from '@emotion/styled';
import { Tooltip, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

import { WIDGET_Z_INDEX } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/ReportWidgets/constants';
import { Lollipop } from '@hotjar/webapp/features/Playbacks/Timeline/Lollipops/Lollipop';
import type { CanvasRef } from '@hotjar/heatmaps';

import { useHighlightsList } from './use-highlights-list';
import { useHeatmapScale } from './use-heatmap-scale';
import { HighlightIndicator } from '../HighlightIndicator';
import { AvatarPopover } from './AvatarPopover';
import { ClickStats } from './ClickStats';

const HALF_AVATAR_HEIGHT = 38 / 2;

interface AvatarOnHeatmapProps {
  canvasRef: React.RefObject<CanvasRef>;
  filters: Parameters<typeof useHighlightsList>[1];
  heatmapWidth: number;
  heatmapHeight: number;
  selectedHighlightId: string | null;
  setSelectedHighlightId: (id: string | null) => void;
  siteId: Parameters<typeof useHighlightsList>[0];
}

const SelectedArea = styled.div<{
  top: number;
  left: number;
  areaWidth: number;
  areaHeight: number;
  isSelected: boolean;
}>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  height: ${(props) => props.areaHeight}px;
  width: ${(props) => props.areaWidth}px;
  border-radius: 2px;
  border: 2px solid transparent;

  ${tokens.transition()};
  transition-property: background-color, border-color;

  ${(props) =>
    props.isSelected &&
    `
    border-color: ${tokens.color.white};
    background-color: transparent;
  `}
`;

const AvatarBox = styled.div<{
  top: number;
  left: number;
  selectedAvatar: 'current' | 'other' | 'none';
}>`
  position: absolute;
  height: 38px;
  width: 38px;
  border-radius: 38px;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  z-index: ${WIDGET_Z_INDEX - 9}; // One index less than HighlightAreaSelectorContainer to be above

  ${tokens.transition()};
  transition-property: opacity, transform;
  transform-origin: left center;

  ${(props) => props.selectedAvatar === 'current' && `transform: scale(1.2);`}
  ${(props) => props.selectedAvatar === 'other' && `opacity: 0.8;`}

  &:hover ~ ${SelectedArea} {
    ${(props) =>
      (props.selectedAvatar === 'none' || props.selectedAvatar === 'other') &&
      `
      border-color: rgba(255, 255, 255, 0.25);
      background-color: rgba(255, 255, 255, 0.1);
    `}
  }

  &:hover ~ .click-stats {
    opacity: 1;
  }
`;

export const AvatarsOnHeatmap = ({
  canvasRef,
  filters,
  heatmapWidth,
  heatmapHeight,
  siteId,
  selectedHighlightId,
  setSelectedHighlightId,
}: AvatarOnHeatmapProps): JSX.Element => {
  const { data } = useHighlightsList(siteId, filters);
  const { t } = useTranslation();
  const scale = useHeatmapScale(heatmapHeight, canvasRef);

  function getSelectedAvatarState(itemId: string): 'current' | 'other' | 'none' {
    if (selectedHighlightId === itemId) {
      return 'current';
    }

    if (selectedHighlightId === null) {
      return 'none';
    }

    return 'other';
  }

  return (
    <>
      {data
        ? data.items.map((item) => {
            const areaWidth = item.selectedArea.x2 - item.selectedArea.x1;
            const areaHeight = item.selectedArea.y2 - item.selectedArea.y1;
            const selectedAvatar = getSelectedAvatarState(item.id);

            return (
              <div key={item.id}>
                <AvatarBox
                  top={(item.selectedArea.y1 + areaHeight / 2 - HALF_AVATAR_HEIGHT) * scale}
                  left={(item.selectedArea.x1 + areaWidth / 2) * scale}
                  selectedAvatar={selectedAvatar}
                >
                  <Tooltip
                    text={t('highlights:heatmaps.avatarTooltip')}
                    isDisabled={selectedAvatar === 'current'}
                  >
                    <Lollipop position={0} timelineWidth={0} showStem={false}>
                      <HighlightIndicator
                        onClick={() => setSelectedHighlightId(item.id)}
                        onMouseEnter={() => null}
                        labels={item.labels.map((label) => label.id)}
                        hasComments={!!item.description}
                        showReactionIcon
                        // FIXME: avatar can be null, this should be fixed in components below
                        author={{ avatar: item.author.avatar as string, name: item.author.name }}
                      />
                    </Lollipop>
                  </Tooltip>
                </AvatarBox>
                <SelectedArea
                  aria-label={t('highlights:heatmaps.highlightArea')}
                  top={item.selectedArea.y1 * scale}
                  left={item.selectedArea.x1 * scale}
                  areaWidth={areaWidth * scale}
                  areaHeight={areaHeight * scale}
                  isSelected={selectedAvatar === 'current'}
                />
                <ClickStats
                  className="click-stats"
                  left={item.selectedArea.x1 * scale}
                  y1={item.selectedArea.y1 * scale}
                  y2={item.selectedArea.y2 * scale}
                  containerHeight={heatmapHeight * scale}
                  containerWidth={heatmapWidth * scale}
                  isDesktop={filters.device === 'desktop'}
                  clicksCount={item.clicksCount ?? 0}
                  clicksPercentage={item.clicksPercentage ?? 0}
                />
                {selectedAvatar === 'current' ? (
                  <AvatarPopover
                    heatmapScale={scale}
                    isDesktop={filters.device === 'desktop'}
                    highlightId={item.id}
                    heatmapWidth={heatmapWidth}
                    heatmapHeight={heatmapHeight}
                    deactivateCurrentItem={() => setSelectedHighlightId(null)}
                  />
                ) : null}
              </div>
            );
          })
        : null}
    </>
  );
};
